import { QueryFunctionOptions, WatchQueryFetchPolicy } from '@apollo/client';
import dayjs from 'dayjs';

import {
  CalendarUnit,
  TimeUnit as ETimeUnit,
  IntegrationFormData as EdgeIntegrationFormData,
  GetAggregatesResponseViewFragment,
  GetDeltaAggregatesResponse,
  GetRecordsResponseViewFragment,
  IntegrationDirection,
} from 'generated/graphql';
import { GroupBy, TimeRange, TimeUnit } from 'v2/core/entities';
import { Analysis } from 'v2/core/entities/analysis';
import { Dashboard } from 'v2/core/entities/dashboard';
import { DashboardUseCase } from 'v2/core/entities/dashboardUseCase';
import { Feedback } from 'v2/core/entities/feedback';
import { SortBy } from 'v2/core/entities/feedback/sortBy';
import { SourceDetail } from 'v2/core/entities/feedback/sources';
import {
  SentimentName,
  SourceName,
  SourceNameWithNamespace,
} from 'v2/core/entities/fields';
import { IntegrationChannel } from 'v2/core/entities/integrations';
import { MatchedFeedbackMetadata } from 'v2/core/entities/labels';
import { CronInput, ReportingType } from 'v2/core/entities/report';
import { Scope } from 'v2/core/entities/scope';
import { SearchCoherenceLevel } from 'v2/core/entities/search';
import { LabelType, TaxonomyEdge } from 'v2/core/entities/taxonomy';
import { Timezone } from 'v2/core/entities/user';
import { UUID } from 'v2/core/entities/uuid';
import { EQLProps } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';

export interface FilterQueryInput {
  TenantID: UUID;
  TaxonomyID?: UUID;
  Where: WhereClause;
  TimeRange: TimeRange;
  Options?: QueryOptions;
}

export interface UseGetSearchRecordsInput extends FilterQueryInput {
  query: string;
  coherenceLevel?: SearchCoherenceLevel;
}

export interface UseGetRecordsInput extends FilterQueryInput {
  highlightType?: 'ENTITY' | 'REASON' | 'SEARCH';
  sortBy: SortBy;
  timezone: Timezone;
}

export interface UseGetAggregatesInput extends FilterQueryInput {
  GroupBy: GroupBy;
}

export interface RelativeOffset {
  value: number;
  unit: ETimeUnit;
}

export interface UseGetDeltaBreakDownAggregatesInput
  extends UseGetAggregatesInput {
  Offset: RelativeOffset;
  RelativeDeltas: boolean;
}

export interface UseGetDeltaTimeSeriesAggregatesInput
  extends UseGetDeltaBreakDownAggregatesInput {
  AggregationWindowInSec: number;
  AggregationCalendarWindow: CalendarUnit;
}

export type QueryFetchPolicy = WatchQueryFetchPolicy;

export interface QueryOptions {
  FetchPolicy?: QueryFetchPolicy;
  Skip?: boolean;
  PollInterval?: number;
  NextFetchPolicy?: QueryFetchPolicy;
  OnCompleted?: QueryFunctionOptions['onCompleted'];
}

export interface UseGetTimeSeriesAggregatesInput extends UseGetAggregatesInput {
  AggregatePeriod: ETimeUnit;
  Timezone: Timezone;
}

// TODO: move this out of repo
export enum QueryStatus {
  NOT_CALLED = 'not_called',
  IN_PROGRESS = 'in_progress',
  ERROR_COMPLETED = 'error_completed',
  SUCCESS_COMPLETED = 'success_completed',
  REFETCHING = 'refetching',
}

export const IsLoading = (q: QueryStatus) => {
  return q === QueryStatus.IN_PROGRESS;
};

export const IsRefetching = (q: QueryStatus) => {
  return q === QueryStatus.REFETCHING;
};

export const IsRefetchingOrLoading = (q: QueryStatus) => {
  return q === QueryStatus.REFETCHING || q === QueryStatus.IN_PROGRESS;
};

const isAnyStatus = (statuses: QueryStatus[], lookup: QueryStatus): boolean => {
  return statuses.find((s) => s === lookup) !== undefined;
};

export const AndStatuses = (statuses: QueryStatus[]): QueryStatus => {
  if (isAnyStatus(statuses, QueryStatus.ERROR_COMPLETED)) {
    return QueryStatus.ERROR_COMPLETED;
  }
  if (isAnyStatus(statuses, QueryStatus.IN_PROGRESS)) {
    return QueryStatus.IN_PROGRESS;
  }
  if (isAnyStatus(statuses, QueryStatus.REFETCHING)) {
    return QueryStatus.REFETCHING;
  }
  if (isAnyStatus(statuses, QueryStatus.NOT_CALLED)) {
    return QueryStatus.NOT_CALLED;
  }

  const isSuccess =
    statuses.filter((s) => QueryStatus.SUCCESS_COMPLETED === s).length ===
    statuses.length;
  if (isSuccess) {
    return QueryStatus.SUCCESS_COMPLETED;
  }
  throw Error('undefined status state ' + JSON.stringify(statuses));
};

export const CreateSkipWithStatus = (
  skip: boolean,
  status: QueryStatus,
): boolean => {
  return skip || status !== QueryStatus.SUCCESS_COMPLETED;
};

export type MetadataField = {
  key: string;
  value: string;
};

export interface IAggDataPointKey {
  metadata?: string;
  labelID?: string;
  source?: SourceNameWithNamespace;
  taxonomyNode?: string;
  language?: string;
  entitySentiment?: SentimentName;
}

export interface ITSDataPointKey extends IAggDataPointKey {
  startAfter: dayjs.Dayjs;
  endBefore: dayjs.Dayjs;
  isExtrapolated?: boolean;
}

export interface IAggDataPoint {
  key: IAggDataPointKey | ITSDataPointKey;
  value: number;
}
export interface IBreakdownData {
  data: IAggDataPoint[];
  total: number;
}

export interface ITimeseriesData {
  data: IAggDataPoint[];
}
export type IAggregateData = IBreakdownData | ITimeseriesData;
export interface IBreakdownQueryResponse {
  data?: IBreakdownData;
  error?: Error;
  status: QueryStatus;
}

export interface ITimeseriesQueryResponse {
  data?: ITimeseriesData;
  error?: Error;
  status: QueryStatus;
}

export interface MatchedFeedback {
  record: { id: string };
  matches?: MatchedFeedbackMetadata[] | undefined;
}

export interface MatchedFeedbackMapType {
  type?: 'SEARCH' | 'ENTITY' | 'REASON';
  matchData: MatchedFeedback[] | undefined;
}

export interface IRecordsQueryResponse {
  data?: Feedback[];
  hits?: number;
  fetchMore: () => Promise<void>;
  isLoadingMore: boolean;
  error?: Error;
  status: QueryStatus;
  id?: UUID;
  refetch?: () => Promise<any>;
}

type MaybeMoreEdgeResponse<HasFetchMore extends boolean = false> =
  HasFetchMore extends true
    ? {
        fetchMore: () => Promise<void>;
        isLoadingMore: boolean;
        hasMore: boolean;
      }
    : {};

export type EdgeResponse<T, HasFetchMore extends boolean = false> = {
  data?: T;
  error?: Error;
  status: QueryStatus;
  refetch?: () => Promise<any>;
} & MaybeMoreEdgeResponse<HasFetchMore>;

export type LazyEdgeResponse<T, Variables> = [
  (options?: { variables: Variables }) => void,
  EdgeResponse<T>,
];

export interface UseCreateThemeInput {
  tenantID: UUID;
  taxonomyID: UUID;
  version: number;
  displayName: string;
  description: string;
  children: UUID[];
}

export interface UseCreateThemeResponse {
  id: UUID;
}

export interface UseUpdateLevelInput {
  id: UUID;
  tenantID: UUID;
  taxonomyID: UUID;
  displayName?: string;
  description?: string;
}

export interface UseUpdateLevelResponse {
  updated: boolean;
}

export interface UseUpdateTaxonomyEdgesInput {
  taxonomyID: UUID;
  version: number;
  tenantID: UUID;
  edgesToAdd?: TaxonomyEdge[];
  edgesToRemove?: TaxonomyEdge[];
}

export interface UseUpdateTaxonomyEdgesResponse {
  updated: boolean;
}

export interface UseListLabelsInput {
  tenantID: UUID;
  type: LabelType;
  includeInvisible?: boolean;
}

type ExecuteEQLProps = Omit<EQLProps, 'sourceDetailsMap'>;

export interface UseExecuteEQLInput {
  tenantID: UUID;
  eqlProps: ExecuteEQLProps;
  cursor?: string;
}

export type ExecuteEQLResponse =
  | GetAggregatesResponseViewFragment
  | GetRecordsResponseViewFragment
  // TODO: added to avoid compilation errors. use fragment when implemented
  | Pick<GetDeltaAggregatesResponse, '__typename'>;

export interface BatchExecuteEQLExpression {
  id: UUID;
  expression: string;
  cursor?: string;
}

export interface BatchExecuteEQLProps {
  id: UUID;
  eqlProps: ExecuteEQLProps;
}

export interface UseBatchExecuteEQLInput {
  tenantID: UUID;
  batches: BatchExecuteEQLProps[];
}

export interface BatchExecuteEQLResponse {
  [id: string]: ExecuteEQLResponse;
}

export interface CreateAnalysisInput {
  analysis: Analysis;
  tenantID: UUID;
  taxonomyID: UUID;
  sourceDetailsMap?: Map<SourceName, SourceDetail>;
}

export interface CreateAnalysisResponse
  extends Pick<Analysis, 'id' | 'viewToken'> {
  editToken: UUID;
}

export interface GetAnalysisInput {
  token: UUID;
  tenantID: UUID;
  userID: UUID;
  sourceDetailsMap?: Map<SourceName, SourceDetail>;
  timezone: Timezone;
  includeScheduleOutbounds?: boolean;
}

export interface UpdateAnalysisInput {
  id: UUID;
  tenantID: UUID;
  updatedAnalysis: Omit<Partial<Analysis>, 'id' | 'tenantID'>;
  taxonomyID: UUID;
  editToken: UUID;
  sourceDetailsMap?: Map<SourceName, SourceDetail>;
  isReportingDisabled?: boolean;
}
export interface UpdateAnalysisResponse {
  updated: boolean;
}

export interface ScheduleInput {
  cron: CronInput;
  outbounds: UUID[];
}

export enum SubjectType {
  Dashboard = 'DASHBOARD',
  Analysis = 'ANALYSIS',
}

export interface UseTestReportInput {
  tenantID: UUID;
  subjectID: UUID;
  subjectType: SubjectType;
  reportType: ReportingType;
  outbounds: UUID[];
}

export interface UseTestReportResponse {
  reportTriggered: boolean;
}

export interface GetDashboardInput {
  token: UUID;
  tenantID: UUID;
  userID: UUID;
  sourceDetailsMap?: Map<SourceName, SourceDetail>;
  timezone: Timezone;
  includeScheduleOutbounds?: boolean;
}

export interface PaginationDetails {
  nextCursor?: string;
  prevCursor?: string;
  hits?: number;
}

export interface UseCreateDashboardInput {
  tenantID: UUID;
  userID: UUID;
  name: string;
  scope: Scope;
  analysisIDs?: UUID[];
  metadata?: string;
  useCase?: DashboardUseCase;
}

export interface CreateDashboardResponse
  extends Pick<Dashboard, 'id' | 'viewToken'> {
  editToken: UUID;
}

export interface UpdatedDashboard extends Partial<Omit<Dashboard, 'analyses'>> {
  id: UUID;
  editToken: UUID;
  analyses?: UUID[];
}

export interface UpdateDashboardInput {
  id: UUID;
  tenantID: UUID;
  userID: UUID;
  updatedDashboard: UpdatedDashboard;
  editToken: UUID;
}

export interface ReportWindowMetadataInput {
  cron: CronInput;
  tenantID: UUID;
  reportType: ReportingType;
  subjectType: SubjectType;
  subjectID: UUID;
  lookBackWindowInHours?: number;
}
export interface ReportWindowMetadata {
  nextTriggerDate?: dayjs.Dayjs;
  recordHitsInLastWindow?: number;
}

export interface UpdateTenantInput {
  tenantID: UUID;
  pinnedDashboardIDs?: UUID[];
}

export interface UpdateTenantResponse {
  updated: boolean;
}

export interface OffsetTime {
  value: number;
  unit: TimeUnit;
}

export interface AnomalyFilterParams {
  changeThreshold: number;
  volumeThreshold: number;
}

export interface IntegrationFormData
  extends Omit<
    EdgeIntegrationFormData,
    'channel' | 'direction' | 'integrationType'
  > {
  channel: IntegrationChannel;
  direction: IntegrationDirection;
  oAuthURL?: string;
}
